import SalviaLayout from "@/components/SalviaLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ChevronLeft, ChevronRight, Save, CheckCircle2, AlertCircle } from "lucide-react";
import { useState } from "react";
import { useLocation, useParams } from "wouter";
import { toast } from "sonner";

interface Question {
  id: number;
  text: string;
  options: { value: number; label: string }[];
}

interface Dimension {
  id: string;
  name: string;
  description: string;
  questions: Question[];
  group: "A" | "B";
}

const ergosQuestions: Dimension[] = [
  {
    id: "pressao_tempo",
    name: "Pressao de Tempo",
    description: "Avalia a intensidade e urgencia das demandas de trabalho",
    group: "A",
    questions: [
      {
        id: 1,
        text: "Com que frequencia voce precisa trabalhar em ritmo acelerado?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 2,
        text: "Com que frequencia voce tem prazos apertados para cumprir?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "atencao",
    name: "Atencao e Concentracao",
    description: "Avalia o nivel de atencao e concentracao exigido",
    group: "A",
    questions: [
      {
        id: 3,
        text: "Seu trabalho exige que voce mantenha atencao constante?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 4,
        text: "Voce precisa estar atento a varios detalhes simultaneamente?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "complexidade",
    name: "Complexidade das Tarefas",
    description: "Avalia a complexidade e dificuldade das atividades",
    group: "A",
    questions: [
      {
        id: 5,
        text: "Suas tarefas exigem conhecimentos tecnicos especializados?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 6,
        text: "Voce precisa resolver problemas complexos no trabalho?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "monotonia",
    name: "Monotonia e Repetitividade",
    description: "Avalia o grau de repeticao e monotonia das tarefas",
    group: "A",
    questions: [
      {
        id: 7,
        text: "Suas tarefas sao repetitivas?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 8,
        text: "Voce realiza as mesmas atividades durante todo o dia?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "raciocinio",
    name: "Raciocinio e Decisao",
    description: "Avalia a necessidade de raciocinio logico e tomada de decisao",
    group: "A",
    questions: [
      {
        id: 9,
        text: "Voce precisa tomar decisoes importantes no trabalho?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 10,
        text: "Suas decisoes tem impacto significativo nos resultados?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "iniciativa",
    name: "Iniciativa e Autonomia",
    description: "Avalia o grau de autonomia e iniciativa no trabalho",
    group: "B",
    questions: [
      {
        id: 11,
        text: "Voce tem liberdade para organizar seu proprio trabalho?",
        options: [
          { value: 5, label: "Nunca ou quase nunca" },
          { value: 4, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 2, label: "Frequentemente" },
          { value: 1, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 12,
        text: "Voce pode propor melhorias no seu trabalho?",
        options: [
          { value: 5, label: "Nunca ou quase nunca" },
          { value: 4, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 2, label: "Frequentemente" },
          { value: 1, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "isolamento",
    name: "Isolamento Social",
    description: "Avalia o nivel de isolamento e contato social no trabalho",
    group: "B",
    questions: [
      {
        id: 13,
        text: "Voce trabalha isolado dos colegas?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 14,
        text: "Voce tem oportunidade de interagir com colegas durante o trabalho?",
        options: [
          { value: 5, label: "Nunca ou quase nunca" },
          { value: 4, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 2, label: "Frequentemente" },
          { value: 1, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "horarios",
    name: "Horarios e Turnos",
    description: "Avalia a organizacao dos horarios e turnos de trabalho",
    group: "B",
    questions: [
      {
        id: 15,
        text: "Voce trabalha em horarios irregulares ou noturnos?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 16,
        text: "Voce faz horas extras com frequencia?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "relacionamento",
    name: "Relacionamento Interpessoal",
    description: "Avalia a qualidade das relacoes no ambiente de trabalho",
    group: "B",
    questions: [
      {
        id: 17,
        text: "Voce tem conflitos com colegas ou superiores?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 18,
        text: "Voce recebe apoio dos colegas quando precisa?",
        options: [
          { value: 5, label: "Nunca ou quase nunca" },
          { value: 4, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 2, label: "Frequentemente" },
          { value: 1, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
  {
    id: "demandas_gerais",
    name: "Demandas Gerais",
    description: "Avalia demandas gerais e condicoes de trabalho",
    group: "B",
    questions: [
      {
        id: 19,
        text: "Voce se sente sobrecarregado com a quantidade de trabalho?",
        options: [
          { value: 1, label: "Nunca ou quase nunca" },
          { value: 2, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 4, label: "Frequentemente" },
          { value: 5, label: "Sempre ou quase sempre" },
        ],
      },
      {
        id: 20,
        text: "Voce consegue equilibrar trabalho e vida pessoal?",
        options: [
          { value: 5, label: "Nunca ou quase nunca" },
          { value: 4, label: "Raramente" },
          { value: 3, label: "As vezes" },
          { value: 2, label: "Frequentemente" },
          { value: 1, label: "Sempre ou quase sempre" },
        ],
      },
    ],
  },
];

export default function Questionnaire() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const [currentDimensionIndex, setCurrentDimensionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});

  const currentDimension = ergosQuestions[currentDimensionIndex];
  const totalQuestions = ergosQuestions.reduce((acc, d) => acc + d.questions.length, 0);
  const answeredQuestions = Object.keys(answers).length;
  const progress = (answeredQuestions / totalQuestions) * 100;

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const canGoNext = currentDimension.questions.every((q) => answers[q.id] !== undefined);

  const handleNext = () => {
    if (currentDimensionIndex < ergosQuestions.length - 1) {
      setCurrentDimensionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentDimensionIndex > 0) {
      setCurrentDimensionIndex((prev) => prev - 1);
    }
  };

  const handleFinish = () => {
    // Calcular resultados
    let totalA = 0;
    let totalB = 0;

    ergosQuestions.forEach((dimension) => {
      const dimensionTotal = dimension.questions.reduce((acc, q) => acc + (answers[q.id] || 0), 0);
      if (dimension.group === "A") {
        totalA += dimensionTotal;
      } else {
        totalB += dimensionTotal;
      }
    });

    const mentalLoad = 0.83 * (totalA + totalB);

    toast.success("Avaliacao concluida com sucesso!");
    setLocation(`/result/${params.id}?totalA=${totalA}&totalB=${totalB}&mentalLoad=${mentalLoad.toFixed(2)}`);
  };

  const isLastDimension = currentDimensionIndex === ergosQuestions.length - 1;
  const allAnswered = answeredQuestions === totalQuestions;

  return (
    <SalviaLayout title="Questionario Ergos">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Progress Header */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-[#002F59]">Avaliacao de Carga Mental</h2>
              <p className="text-sm text-gray-500">Cargo: Desenvolvedor | Setor: TI</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Progresso</p>
              <p className="text-2xl font-bold text-[#002F59]">{Math.round(progress)}%</p>
            </div>
          </div>
          <Progress value={progress} className="h-3" />
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span>{answeredQuestions} de {totalQuestions} perguntas respondidas</span>
            <span>Dimensao {currentDimensionIndex + 1} de {ergosQuestions.length}</span>
          </div>
        </Card>

        {/* Dimension Navigation */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {ergosQuestions.map((dim, index) => {
            const isComplete = dim.questions.every((q) => answers[q.id] !== undefined);
            const isCurrent = index === currentDimensionIndex;
            return (
              <button
                key={dim.id}
                onClick={() => setCurrentDimensionIndex(index)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  isCurrent
                    ? "bg-[#002F59] text-white"
                    : isComplete
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {isComplete && <CheckCircle2 size={14} className="inline mr-1" />}
                {dim.name}
              </button>
            );
          })}
        </div>

        {/* Current Dimension */}
        <Card className="p-6">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                currentDimension.group === "A" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
              }`}>
                Grupo {currentDimension.group}
              </span>
              <h3 className="text-xl font-bold text-[#002F59]">{currentDimension.name}</h3>
            </div>
            <p className="text-gray-600">{currentDimension.description}</p>
          </div>

          <div className="space-y-8">
            {currentDimension.questions.map((question, qIndex) => (
              <div key={question.id} className="space-y-4">
                <p className="font-semibold text-gray-900">
                  {qIndex + 1}. {question.text}
                </p>
                <div className="grid gap-2">
                  {question.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(question.id, option.value)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        answers[question.id] === option.value
                          ? "border-[#C0FF00] bg-[#C0FF00]/10"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            answers[question.id] === option.value
                              ? "border-[#C0FF00] bg-[#C0FF00]"
                              : "border-gray-300"
                          }`}
                        >
                          {answers[question.id] === option.value && (
                            <div className="w-2 h-2 rounded-full bg-[#002F59]" />
                          )}
                        </div>
                        <span className="text-gray-700">{option.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentDimensionIndex === 0}
          >
            <ChevronLeft size={20} className="mr-2" />
            Anterior
          </Button>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => toast.info("Progresso salvo!")}>
              <Save size={20} className="mr-2" />
              Salvar
            </Button>

            {isLastDimension ? (
              <Button
                className="bg-[#C0FF00] hover:bg-[#a8e600] text-[#002F59]"
                onClick={handleFinish}
                disabled={!allAnswered}
              >
                <CheckCircle2 size={20} className="mr-2" />
                Finalizar Avaliacao
              </Button>
            ) : (
              <Button
                className="bg-[#002F59] hover:bg-[#001F3F] text-white"
                onClick={handleNext}
                disabled={!canGoNext}
              >
                Proxima Dimensao
                <ChevronRight size={20} className="ml-2" />
              </Button>
            )}
          </div>
        </div>

        {!canGoNext && (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-4 rounded-lg">
            <AlertCircle size={20} />
            <span className="text-sm">Responda todas as perguntas desta dimensao para continuar.</span>
          </div>
        )}
      </div>
    </SalviaLayout>
  );
}
